import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const Budget = () => {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", type: "annual", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchFinancialYears(); }, []);
  useEffect(() => { if (selectedFY) fetchBudgets(); }, [selectedFY]);

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1);
      setSelectedFY(String(cur?.id || res.details?.[0]?.id || ""));
    }
  };

  const fetchBudgets = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listBudgets({ financial_year_id: parseInt(selectedFY) });
    if (res.status === "OK") setBudgets(res.details?.list || (Array.isArray(res.details) ? res.details : []));
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.name) { toast.error("Budget name required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.createBudget({ ...form, financial_year_id: parseInt(selectedFY) });
    if (res.status === "OK") { toast.success("Budget created!"); fetchBudgets(); setShowModal(false); setForm({ name: "", type: "annual", description: "" }); }
    else toast.error(res.message);
    setSaving(false);
  };

  const statusBadge = (status) => {
    const map = { draft: "secondary", active: "success", closed: "dark" };
    return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
  };

  const columns = [
    { key: "name", label: "Budget Name", render: (v) => <strong>{v}</strong> },
    { key: "type", label: "Type", render: (v) => <span className="badge bg-info">{v}</span> },
    { key: "status", label: "Status", render: (v) => statusBadge(v) },
    { key: "total_budgeted", label: "Total Budgeted", render: (v) => functions.formatCurrency(v) },
    { key: "total_actual", label: "Actual Spent", render: (v) => functions.formatCurrency(v) },
    { key: "variance", label: "Variance", render: (v) => <span className={v >= 0 ? "tx-success" : "tx-danger"}>{functions.formatCurrency(v)}</span> },
    {
      key: "id", label: "Actions",
      render: (id) => (
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/budget/${id}`)}><i className="fa-solid fa-eye"></i></button>
          <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/accounting/budget/${id}/vs-actual`)} title="Budget vs Actual"><i className="fa-solid fa-chart-bar"></i></button>
        </div>
      ),
    },
  ];

  return (
    <AppContainer
      title="Budget Management"
      previousPageTitle="Accounting"
      description="Create and monitor budgets for each financial year. Track actual spending vs budgeted amounts per account. Variance = Budgeted minus Actual."
      actions={[
        { label: "New Budget", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowModal(true) },
        { label: "Budget vs Actual", icon: "fa-solid fa-chart-bar", className: "btn btn-outline-secondary", onClick: () => navigate("/accounting/budget/vs-actual") },
      ]}
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select2 options={financialYears.map((y) => ({ value: String(y.id), label: y.name }))} value={selectedFY} onChange={setSelectedFY} placeholder="Select Year" />
          </div>
        </div>
      </div>
      <div className="card custom-card p-3">
        <DataTable columns={columns} data={budgets} loading={loading} pageSize={10} emptyMessage="No budgets created yet." />
      </div>

      {showModal && (
        <AppModal
          title="Create Budget"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Creating..." : "Create Budget"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Budget Name <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Operations Budget 2025/26" />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Type</label>
              <Select2 options={[{ value: "annual", label: "Annual" }, { value: "quarterly", label: "Quarterly" }, { value: "monthly", label: "Monthly" }]} value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default Budget;
